import { Component, Output, EventEmitter, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { DialogService } from 'src/app/home/services/dialog.service';
import { AuthDialogType, AuthTypes, RegistrationOption } from 'src/app/home/interfaces/auth.interface';
import { TranslocoService } from '@ngneat/transloco';
import { MatDialog } from '@angular/material/dialog';
import { ShopService } from 'src/app/home/services/shop.service';
import { normalizePhoneCountryCode } from 'src/app/utils/phone-utils';
import { AuthService } from 'src/app/home/services/auth.service';
import { SnackbarService, SnackbarType } from 'src/app/home/services/snackbar.service';
import { isUserMissingData } from 'src/app/utils/isUserMissingData';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-auth-form-sign-up-with',
  templateUrl: './auth-form-sign-up-with.component.html',
  styleUrls: ['./auth-form-sign-up-with.component.scss', '../auth-dialog.component.scss'],
  standalone: false,
})
export class AuthFormSignUpWithComponent implements OnInit, OnDestroy {
  @Output() stepChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() countryPrefixChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() loginSuccess: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() changeAuthDialogType = new EventEmitter<{ type: AuthDialogType; existingAccount?: boolean }>();
  @Input() form: FormGroup;

  destroy$ = new Subject<boolean>();
  loading = false;
  _validationLoading = false;
  public set validationLoading(v: boolean) {
    this._validationLoading = v;
    if (!this.validationLoading) {
      this.customErrors.validationInProgress = false;
    }
  }

  public get validationLoading(): boolean {
    return this._validationLoading;
  }

  customErrors = {
    emptyCaptcha: false,
    invalidCaptcha: false,
    invalidEmail: false,
    userExists: false,
    validationInProgress: false,
  };

  countryPrefix: string;
  phoneCode: number;
  authType = AuthTypes.Register;
  assetsDir = '/';
  constructor(
    /* private fbLoginService: FbLoginService, */
    private dialogService: DialogService,
    private translateService: TranslocoService,
    private dialogRef: MatDialog,
    public shopService: ShopService,
    private authService: AuthService,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    if (environment.production) {
      this.assetsDir = environment.deploy_url;
    }
    sessionStorage.setItem('activeRegistrationOption', RegistrationOption.Email);
  }

  getFormControl(name: string) {
    return this.form.get(name);
  }

  getErrorKeys(controlName: string): string[] {
    const control = this.getFormControl(controlName);
    return Object.keys(control.errors);
  }

  loginWithFacebook() {
    this.loading = true;
    /*  this.fbLoginService.facebookRedirect(); */
    this.dialogService.closeDialogs();
  }

  loginWithGoogle(credential: string) {
    this.loading = true;
    this.authService.loginWithGoogle(credential, this.getLanguage()).subscribe({
      // Google auto-creates the account on first login; route to profile-completion only if data is missing.
      next: (user) => {
        if (isUserMissingData(user)) {
          this.changeAuthDialogType.emit({ type: AuthDialogType.UserProfileDetails, existingAccount: true });
        } else {
          this.loginSuccess.emit(true);
          this.dialogRef.closeAll();
        }
      },
      error: (error) => {
        this.loading = false;
        this.snackbarService.openSnackbar(SnackbarType.Error, { data: { msg: 'global.google_login_failed', translate: true } });
        Sentry.captureException(error?.error || error, {
          tags: {
            feature: 'auth-dialog',
            method: 'google-login',
          },
        });
      },
    });
  }

  onGoogleFailed() {
    this.snackbarService.openSnackbar(SnackbarType.Error, { data: { msg: 'global.something_went_wrong', translate: true } });
  }

  changeStep() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.valid && !this.validationLoading) {
      if (this.form.get('phone_number')?.value) {
        this.form.addControl('phone_country_code', new FormControl(normalizePhoneCountryCode(this.countryPrefix)));
      }

      this.stepChange.emit();
    } else if (this.validationLoading) {
      this.customErrors.validationInProgress = true;
    }
  }

  onRegistrationOptionChange(option: RegistrationOption) {
    sessionStorage.setItem('activeRegistrationOption', option);
  }

  onCountryPrefixChange(prefix: string) {
    this.countryPrefix = normalizePhoneCountryCode(prefix);
  }

  onCaptchaChange() {
    this.customErrors.emptyCaptcha = false;
    this.customErrors.invalidCaptcha = false;
  }

  get registrationOptions() {
    return RegistrationOption;
  }

  closeDialog() {
    this.dialogRef.closeAll();
  }

  getLanguage(): 'nl' | 'en' {
    const currentLang = this.translateService.getActiveLang();
    return currentLang === 'nl' ? currentLang : 'en';
  }

  setFacebookRedirect(param) {
    this.shopService.facebookRedirect = param;
  }
  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
